/**
 * Открытые алерты и их подтверждение. Единственная часть вкладки «Мониторинг», которая
 * работает на живых данных уже в фазе 1: `alert` наполняется `raise()` бэкенда с первого
 * дня, в отличие от представлений первых прогонов (08-alerts.md §12).
 *
 * СЕМАНТИКА ПОДТВЕРЖДЕНИЯ НОРМАТИВНА И НЕ СОВПАДАЕТ С ИНТУИТИВНОЙ (09 §1.15):
 * `acked_at_ms` не меняет `state`. Подтверждённый алерт остаётся открытым, продолжает копить
 * `occurrences` и держать `dedup_key` — он лишь уходит из выборки `?status=open`. Кнопка
 * поэтому называется «Подтвердить», а не «Закрыть», и рядом с ней стоит текст про это.
 * Человек, прочитавший «закрыть», решил бы, что причина устранена.
 *
 * Данные компонент грузит сам, а не получает пропсами: у списка ровно один потребитель, а
 * после подтверждения обновлять надо ровно его — владелец данных и владелец мутации здесь
 * совпадают (в отличие от бэклога, где решение меняет и список, и панель).
 *
 * Повторное подтверждение даёт 404 (бэкенд не различает «нет алерта» и «уже подтверждён» —
 * это одно состояние). Для человека это не ошибка: показываем словами и обновляем список.
 *
 * ЗАГОЛОВКА У КОМПОНЕНТА БОЛЬШЕ НЕТ (правка 07.09.2026). Раньше он рисовал свою карточку с
 * «Требует внимания» и счётчиком; теперь этот заголовок несёт сворачиваемый раздел вкладки
 * мониторинга — он же держит бейдж по важности, видимый и в свёрнутом виде. Двух заголовков
 * с одним словом на экране быть не должно, поэтому здесь осталось ТЕЛО раздела: пояснение к
 * подтверждению, ошибки и сами строки. Числа уезжают наверх через `onSummary` — вызывающий
 * рисует по ним бейдж и решает, разворачивать ли раздел.
 *
 * ВРЕМЯ — В ЗОНЕ ПОКАЗА ИНТЕРФЕЙСА (`shortMsk`, `components/format.ts`). До 08.09.2026 здесь
 * стоял свой, третий по счёту, слайсер UTC, и обоснование у него было настоящее: местная
 * минута требует зоны бренда, а у операционной вкладки брендов может быть несколько.
 * Изменилось не это, а решение (Илья 07.09.2026): зона показа зашита одна на весь UI и
 * подписана у каждого момента. Копия нарезки удалена вместе со своим обоснованием —
 * четвёртой копии в этом пакете быть не должно.
 */

import { type JSX, useEffect, useState } from 'react';
import { ApiRequestError, post } from '../api/client.ts';
import { useResource } from '../api/useResource.ts';
import { plural, shortMsk } from './format.ts';
import { IconWarn } from './icons.tsx';

/**
 * Строка ответа `GET /api/v1/alerts` — форма снята с `AlertListItem`
 * (services/backend/src/domain/alerts-read.ts). Схемы в `@smm/contracts` на этот маршрут нет,
 * поэтому тип описан здесь и только здесь; заводить второй экземпляр в другом файле нельзя.
 * `severity`/`kind`/`state` оставлены строками намеренно: значение приходит по HTTP, и
 * незнакомое не должно ронять экран — оно должно быть видно.
 */
export interface AlertListItem {
  readonly id: string;
  readonly type: string;
  readonly kind: string;
  readonly severity: string;
  readonly brand: string | null;
  readonly state: string;
  readonly title: string;
  readonly detail: string | null;
  readonly suggested_action: string | null;
  readonly occurrences: number;
  readonly created_at: string;
  readonly last_seen_at: string;
  readonly acked_at: string | null;
}

/**
 * Сводка для заголовка раздела. Наверх уходят ТОЛЬКО числа: свёрнутый раздел обязан
 * показывать ФАКТ проблемы («2 критично · 1 предупреждение»), а подробности — это уже тело.
 *
 * `state` различает «ещё грузим» и «загрузились, и пусто»: без этого различения заголовок
 * писал бы «открытых алертов нет» в первую секунду каждой загрузки — то есть ровно то
 * утверждение, ради ложности которого экран и построен. Незнакомая важность попадает в
 * `total`, но ни в один из именованных счётчиков: выдумывать ей место в шкале нельзя.
 */
export interface AlertsSummary {
  readonly state: 'loading' | 'error' | 'ready';
  readonly total: number;
  readonly critical: number;
  readonly error: number;
  readonly warning: number;
}

interface SeverityView {
  readonly label: string;
  /** Класс точки из base.css либо пустая строка — тогда цвет задаётся токеном инлайном. */
  readonly dot: string;
  readonly color: string;
}

/**
 * Шкала важности — та же, что в реестре алертов (`ALERT_SEVERITIES`), но её русские имена
 * и цвета живут здесь: в контрактах их нет и быть не должно, это оформление.
 * `ReadonlyMap`, а не `Record`, потому что ключ приходит по проводу строкой.
 */
const SEVERITY: ReadonlyMap<string, SeverityView> = new Map<string, SeverityView>([
  ['critical', { label: 'критично', dot: 'dot-err', color: 'var(--red-ink)' }],
  ['error', { label: 'ошибка', dot: 'dot-err', color: 'var(--red-ink)' }],
  ['warning', { label: 'предупреждение', dot: 'dot-warn', color: 'var(--amber-ink)' }],
  ['info', { label: 'сведения', dot: '', color: 'var(--muted)' }],
]);

const UNKNOWN_SEVERITY: SeverityView = {
  label: 'важность неизвестна',
  dot: '',
  color: 'var(--muted)',
};

/**
 * Потолок ОТРИСОВКИ, а не выборки. Двадцать строк алертов — это тот самый «длинный коридор»,
 * из-за которого раздел и сворачивают; но понижать `limit` запроса нельзя: тогда до
 * остальных алертов оператор не дотянулся бы вовсе, а счётчик в заголовке начал бы врать.
 * Поэтому запрашиваем столько же, а рисуем первые восемь с кнопкой «показать все N».
 */
const ROWS_SHOWN = 8;

export interface AlertsListProps {
  /** Потолок выборки. Маршрут принимает 1..500, по умолчанию 100. */
  readonly limit?: number;
  /** Сводка для заголовка раздела; зовётся при изменении ЧИСЕЛ, а не на каждый рендер. */
  readonly onSummary?: (summary: AlertsSummary) => void;
}

export function AlertsList({ limit = 50, onSummary }: AlertsListProps): JSX.Element {
  const alerts = useResource<AlertListItem[]>(`/api/v1/alerts?status=open&limit=${limit}`);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState<boolean>(false);

  const { refetch } = alerts;

  const ack = (id: string): void => {
    if (busyId !== null) return;
    setBusyId(id);
    setError(null);
    post<void>(`/api/v1/alerts/${id}/ack`)
      .then(() => {
        refetch();
      })
      .catch((e: unknown) => {
        if (e instanceof ApiRequestError && e.status === 404) {
          setError('Этот алерт уже подтверждён — список обновлён.');
          refetch();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setBusyId(null);
      });
  };

  const rows = alerts.data ?? [];
  const countOf = (severity: string): number => rows.filter((a) => a.severity === severity).length;

  /*
   * Состояние выводится из ДАННЫХ, а не из `loading`: при перечитывании после подтверждения
   * `loading` снова истинно, а числа в заголовке обязаны остаться прежними — иначе бейдж
   * мигал бы «проверяем» на каждое нажатие «Подтвердить».
   */
  const state: AlertsSummary['state'] =
    alerts.error !== undefined ? 'error' : alerts.data === undefined ? 'loading' : 'ready';
  const total = rows.length;
  const critical = countOf('critical');
  const errorCount = countOf('error');
  const warning = countOf('warning');

  /*
   * Наверх уходят ПРИМИТИВЫ, а объект собирается внутри эффекта. Собери мы его в теле
   * компонента, ссылка была бы новой на каждый рендер, эффект срабатывал бы каждый раз, и
   * `setState` вызывающего замкнул бы цикл перерисовки.
   */
  useEffect(() => {
    onSummary?.({ state, total, critical, error: errorCount, warning });
  }, [onSummary, state, total, critical, errorCount, warning]);

  const shown = showAll ? rows : rows.slice(0, ROWS_SHOWN);

  return (
    <div className="stack" style={{ gap: 10 }}>
      {/*
        Пояснение стоит В ТЕЛЕ раздела, а не в заголовке: это инструкция тому, кто уже
        собрался нажать кнопку, а не факт, который надо видеть из свёрнутого состояния.
      */}
      <div className="micro">
        Подтверждение не закрывает алерт: оно снимает его из «требует внимания». Условие остаётся
        открытым и продолжает копить повторы, пока причина не устранена.
      </div>

      {error === null ? null : (
        <div className="val val-warn">
          <IconWarn />
          <span>{error}</span>
        </div>
      )}

      {alerts.loading ? <div className="micro">Загружаем алерты…</div> : null}

      {alerts.error === undefined ? null : (
        <div className="val val-err">
          <span>Список алертов не загрузился: {alerts.error.message}</span>
        </div>
      )}

      {!alerts.loading && alerts.error === undefined && rows.length === 0 ? (
        <div className="val val-info">
          <span>
            Открытых алертов нет. Подтверждённые остаются в базе и видны по фильтру acked.
          </span>
        </div>
      ) : null}

      {rows.length === 0 ? null : (
        <div className="stack">
          {shown.map((a) => {
            const sev = SEVERITY.get(a.severity) ?? UNKNOWN_SEVERITY;
            return (
              <div
                key={a.id}
                className="vrow"
                style={{ gap: 12, alignItems: 'flex-start', padding: '12px 0' }}
              >
                <span
                  className={sev.dot === '' ? 'dot' : `dot ${sev.dot}`}
                  style={{
                    marginTop: 6,
                    ...(sev.dot === '' ? { background: 'var(--muted)' } : {}),
                  }}
                />
                <div className="stack" style={{ gap: 4, flex: 1, minWidth: 0 }}>
                  <span className="small" style={{ fontWeight: 600, color: 'var(--ink)' }}>
                    {a.title}
                  </span>
                  {a.detail === null ? null : <span className="micro">{a.detail}</span>}
                  {a.suggested_action === null ? null : (
                    <span className="micro">Что делать: {a.suggested_action}</span>
                  )}
                  <span className="meta">
                    <span style={{ color: sev.color }}>{sev.label}</span> · {a.type} · повторов{' '}
                    {a.occurrences} · с {shortMsk(a.created_at)} · последний{' '}
                    {shortMsk(a.last_seen_at)} ·{' '}
                    {a.brand === null ? 'весь контур' : `бренд ${a.brand}`}
                  </span>
                </div>
                <button
                  type="button"
                  className={busyId === null ? 'btn btn-sm btn-ghost' : 'btn btn-sm btn-dis'}
                  disabled={busyId !== null}
                  onClick={() => {
                    ack(a.id);
                  }}
                >
                  Подтвердить
                </button>
              </div>
            );
          })}

          {showAll || rows.length <= ROWS_SHOWN ? null : (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => {
                setShowAll(true);
              }}
            >
              Показать все {rows.length} {plural(rows.length, 'алерт', 'алерта', 'алертов')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
